'use client';
import { useEffect, useState, useCallback, useRef } from 'react';
import { StockFactors, PillarScores, ScoredStock } from '@/lib/compute';
import { ALL_QUALITY_KEYS, ALL_VALUATION_KEYS } from '@/lib/clientScore';
import { DEFAULT_PILLAR_WEIGHTS } from '@/lib/factor-config';
import { ProjectionHorizon } from '@/lib/projectScore';
import { ControlPanel } from '@/components/ControlPanel';
import { AppNav } from '@/components/AppNav';
import { AppHomeHeader } from '@/components/AppHomeHeader';
import { HomeViewport } from '@/components/HomeViewport';
import staticModelStats from '@/data/model-stats.json';
import { useVisualizerData } from '@/lib/hooks/use-visualizer-data';
import { useStocksToUse } from '@/lib/hooks/use-stocks-to-use';
import { useResource, invalidateResource } from '@/lib/use-resource';
import { useHomeDerived } from '@/lib/hooks/use-home-derived';

const DEFAULT_ALL = new Set<keyof StockFactors>([...ALL_QUALITY_KEYS, ...ALL_VALUATION_KEYS]);

export default function Home() {
  const {
    data,
    error,
    backtestData,
    technicalData,
    technicalWeeklyData,
    technicalMonthlyData,
    enteredAlphaSymbols,
    exitedAlphaSymbols,
    loadHistorical,
  } = useVisualizerData();
  const [selectedSectors, setSelectedSectors] = useState<Set<string>>(new Set());
  const [selectedIndustries, setSelectedIndustries] = useState<Set<string>>(new Set());
  const [minMarketCap, setMinMarketCap] = useState(0);
  const [maxMarketCap, setMaxMarketCap] = useState(1e13);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'heatmap' | 'table' | 'backtest' | 'technical' | '3d'>('3d');
  const [pinnedSymbols, setPinnedSymbols] = useState<Set<string>>(new Set());
  const [enabledFactors, setEnabledFactors] = useState<Set<keyof StockFactors>>(DEFAULT_ALL);
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  // Deep-link: /?symbol=VST selects a name on load. Used by the live-presentation
  // "Update" screenshotter to frame a specific stock on the scatter. This reads an
  // external system (the URL) once after mount — a legitimate effect-setState, kept
  // in an effect (not a lazy useState init) to avoid an SSR/hydration mismatch.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sym = new URL(window.location.href).searchParams.get('symbol');
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (sym) setSelectedSymbol(sym.toUpperCase());
  }, []);
  // V-axis toggle. Production V is BLENDED (0.75 peer-z + 0.25 universe-z
  // absolute, Definition C). scores.json carries `valuationScoreBlended`
  // written by blend-into-scores.ts and alpha-basket selection uses it. The
  // toggle defaults to 'blended' (production) and can flip to 'classic' for
  // a peer-z-only view.
  const [vMode, setVMode] = useState<'classic' | 'blended'>('blended');
  // Alpha Zone isolation — bypasses sector/industry/marketCap filters and
  // shows ONLY the live alpha basket (Classic mode) or the live top-20 by
  // blended composite (Blended mode). Mutually exclusive with portfolio
  // isolation; toggling one clears the other.
  const [alphaZoneIsolated, setAlphaZoneIsolated] = useState(false);
  const [portfolioSymbols, setPortfolioSymbols] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set<string>();
    try {
      const saved = localStorage.getItem('triumph-portfolio');
      return saved ? new Set(JSON.parse(saved)) : new Set<string>();
    } catch { return new Set<string>(); }
  });

  // Live model stats — `/api/model-stats` reads `data/model-stats.json` via fs at request
  // time (force-dynamic). Falls back to the bundled static import on first paint or if the
  // route ever 404s. Both shapes are identical so the consumer doesn't care which it got.
  const { data: liveModelStats } = useResource<typeof staticModelStats>('/api/model-stats');
  const modelStats = liveModelStats ?? staticModelStats;

  // Hydrate portfolio from server on mount — authoritative source for cross-device + cron access.
  // Shared via useResource cache with every other component reading /api/portfolio.
  const { data: portfolioRemote } = useResource<{ symbols?: string[] }>('/api/portfolio');
  const lastRemoteRef = useRef<readonly string[] | null>(null);
  useEffect(() => {
    const next = portfolioRemote?.symbols;
    if (!Array.isArray(next) || next.length === 0) return;
    // useResource returns the same reference on cache hit, so this guard
    // means we only setState once per actual server change. Without the ref
    // each parent re-render that re-runs the effect would re-set local
    // state to the same payload (cascading-render warning). The lint rule
    // calls this out by default; the ref-gate is the standard escape.
    if (lastRemoteRef.current === next) return;
    lastRemoteRef.current = next;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPortfolioSymbols(new Set<string>(next));
    try { localStorage.setItem('triumph-portfolio', JSON.stringify(next)); } catch { /* localStorage disabled */ }
  }, [portfolioRemote]);
  const [portfolioIsolated, setPortfolioIsolated] = useState(false);
  const [highlightedFactor, setHighlightedFactor] = useState<keyof StockFactors | null>(null);
  const [highlightedPillar, setHighlightedPillar] = useState<keyof PillarScores | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [historicalPeriod, setHistoricalPeriod] = useState<string | null>(null);
  const [historicalStocks, setHistoricalStocks] = useState<ScoredStock[] | null>(null);
  const [historicalDate, setHistoricalDate] = useState<string | null>(null);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [pillarView, setPillarView] = useState<string | null>(null);
  const [projectionHorizon, setProjectionHorizon] = useState<ProjectionHorizon>('now');
  const [pillarWeights, setPillarWeights] = useState<Record<keyof PillarScores, number>>(DEFAULT_PILLAR_WEIGHTS);

  // When stocks data first lands, default selectedSectors to "all sectors".
  // useResource returns a stable reference until the URL response actually
  // changes, so this fires once at mount and again only on a real refresh —
  // gating on the ref makes the no-op case explicit (lint rule otherwise
  // flags this as cascading-render).
  const lastDataRef = useRef<typeof data>(undefined);
  useEffect(() => {
    if (!data?.stocks) return;
    if (lastDataRef.current === data) return;
    lastDataRef.current = data;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedSectors(new Set([...data.stocks.map((s) => s.sector), 'Canada']));
  }, [data]);

  const {
    sectors,
    industriesBySector,
    stocksToUse: rawStocksToUse,
  } = useStocksToUse({
    data,
    enabledFactors,
    defaultAllSize: DEFAULT_ALL.size,
    pillarWeights,
    defaultWeights: DEFAULT_PILLAR_WEIGHTS,
    projectionHorizon,
  });

  const {
    stocksToUse,
    alphaZoneStocks,
    filtered,
    factorHighlightStocks,
    sectorTopComposite,
    filteredHistorical,
    selectedStock,
    selectedTechnicalData,
  } = useHomeDerived({
    rawStocksToUse, vMode,
    selectedSectors, selectedIndustries, minMarketCap, maxMarketCap,
    portfolioIsolated, portfolioSymbols, alphaZoneIsolated,
    highlightedFactor, highlightedPillar,
    selectedSymbol,
    technicalData, technicalWeeklyData, technicalMonthlyData,
    historicalStocks,
  });

  const toggleSector = (s: string) => {
    setSelectedSectors(prev => {
      // If this is the only selected sector, re-select all
      if (prev.size === 1 && prev.has(s)) {
        setSelectedIndustries(new Set());
        return new Set(sectors);
      }
      // Otherwise isolate to just this sector — clear any industry filter
      // since the user is changing the parent context.
      setSelectedIndustries(new Set());
      return new Set([s]);
    });
  };

  const toggleIndustry = (ind: string) => {
    setSelectedIndustries(prev => {
      const next = new Set(prev);
      if (next.has(ind)) next.delete(ind); else next.add(ind);
      return next;
    });
  };

  const selectAllSectors = useCallback(() => {
    setSelectedSectors(new Set(sectors));
    setSelectedIndustries(new Set());
  }, [sectors]);

  // Hard reset to the unfiltered, unhighlighted "complete view".
  // Wired to the top-nav 3D button so it works as a home button.
  // Does NOT reset model parameters (pillar weights, enabled factors) — those
  // are intentional user customizations, not filters/highlights.
  const resetToCompleteView = useCallback(() => {
    setSelectedSectors(new Set(sectors));
    setSelectedIndustries(new Set());
    setMinMarketCap(0);
    setMaxMarketCap(1e13);
    setPortfolioIsolated(false);
    setHighlightedFactor(null);
    setHighlightedPillar(null);
    setPillarView(null);
    setSelectedSymbol(null);
    setSearch('');
    setResetKey(k => k + 1);
  }, [sectors]);

  const togglePin = useCallback((symbol: string) => {
    setPinnedSymbols(prev => {
      const next = new Set(prev);
      if (next.has(symbol)) {
        next.delete(symbol);
      } else if (next.size < 5) {
        next.add(symbol);
      }
      return next;
    });
  }, []);

  const toggleFactor = useCallback((key: keyof StockFactors) => {
    setEnabledFactors(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const resetFactors = useCallback(() => {
    setEnabledFactors(DEFAULT_ALL);
  }, []);

  const handlePortfolioChange = useCallback((symbols: Set<string>) => {
    setPortfolioSymbols(symbols);
    const arr = [...symbols];
    try { localStorage.setItem('triumph-portfolio', JSON.stringify(arr)); } catch {}
    fetch('/api/portfolio', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ symbols: arr }),
    })
      // Server rewrote the portfolio JSON — drop the cached GET so the
      // next consumer reads the fresh shape instead of the stale snapshot.
      .then(() => invalidateResource('/api/portfolio'))
      .catch(() => {});
    if (symbols.size === 0) setPortfolioIsolated(false);
  }, []);

  const onPillarWeightChange = useCallback((pillar: keyof PillarScores, value: number) => {
    setPillarWeights(prev => {
      const next = { ...prev };
      next[pillar] = value;
      const remaining = 100 - value;
      const otherKeys = (Object.keys(prev) as (keyof PillarScores)[]).filter(k => k !== pillar);
      const otherSum = otherKeys.reduce((sum, k) => sum + prev[k], 0);
      if (otherSum > 0) {
        const scale = remaining / otherSum;
        for (const k of otherKeys) next[k] = Math.round(prev[k] * scale);
      } else {
        const each = Math.floor(remaining / otherKeys.length);
        for (const k of otherKeys) next[k] = each;
      }
      // Fix rounding errors on last key
      const total = Object.values(next).reduce((a, b) => a + b, 0);
      if (total !== 100) {
        const last = otherKeys[otherKeys.length - 1];
        next[last] += 100 - total;
      }
      return next;
    });
  }, []);

  const handleHistoricalPeriod = useCallback((period: string | null) => {
    if (!period) {
      setHistoricalPeriod(null);
      setHistoricalStocks(null);
      setHistoricalDate(null);
      return;
    }
    setHistoricalPeriod(period);
    setHistoricalLoading(true);
    loadHistorical(period)
      .then(({ stocks, date }) => {
        setHistoricalStocks(stocks);
        setHistoricalDate(date);
      })
      .finally(() => setHistoricalLoading(false));
  }, [loadHistorical]);

  const resetWeights = useCallback(() => {
    setPillarWeights({ ...DEFAULT_PILLAR_WEIGHTS });
  }, []);

  const selectStock = useCallback((symbol: string) => {
    setSelectedSymbol(prev => prev === symbol ? null : symbol);
  }, []);

  // Splash removed 2026-04-29. Main UI
  // shows immediately on mount.
  const mainVisible = true;

  if (error) return (
    <div className="flex items-center justify-center h-screen" style={{ background: '#0a0c10' }}>
      <div className="text-center max-w-sm">
        <div className="text-red-400 text-lg font-medium mb-2">Error</div>
        <div className="text-[#e2e8f0] text-sm">{error}</div>
      </div>
    </div>
  );

  return (
    <>
    <div
      className="flex flex-col h-screen overflow-hidden relative"
      style={{
        background: '#0a0c10',
        opacity: mainVisible ? 1 : 0,
        transition: 'opacity 1s ease-in',
      }}
    >
      <AppNav />
      <AppHomeHeader
        viewMode={viewMode}
        factorCount={modelStats.factorCount}
        updatedAt={data?.updatedAt}
        onSelect3D={() => {
          resetToCompleteView();
          setViewMode('3d');
        }}
        onSelectOther={(mode) => {
          setPillarView(null);
          setHighlightedFactor(null);
          setHighlightedPillar(null);
          setSelectedSymbol(null);
          setSearch('');
          setResetKey(k => k + 1);
          setViewMode(mode);
        }}
      />

      <div className="flex flex-1 overflow-hidden relative">
      {/* Mobile hamburger button */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-[#0d1117]/90 border border-[#1e293b] backdrop-blur-sm"
        aria-label="Open menu"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#e2e8f0" strokeWidth="1.5" strokeLinecap="round">
          <line x1="3" y1="5" x2="17" y2="5" />
          <line x1="3" y1="10" x2="17" y2="10" />
          <line x1="3" y1="15" x2="17" y2="15" />
        </svg>
      </button>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar wrapper — slide-in on mobile, always visible on desktop */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-[300px] transform transition-transform duration-300 ease-in-out
        md:static md:w-[340px] md:flex-shrink-0 md:transform-none md:transition-none
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <ControlPanel
        sectors={sectors}
        selectedSectors={selectedSectors}
        onSectorToggle={toggleSector}
        selectedIndustries={selectedIndustries}
        onIndustryToggle={toggleIndustry}
        onSelectAllSectors={selectAllSectors}
        industriesBySector={industriesBySector}
        minMarketCap={minMarketCap}
        maxMarketCap={maxMarketCap}
        onMinMarketCapChange={setMinMarketCap}
        onMaxMarketCapChange={setMaxMarketCap}
        search={search}
        onSearch={setSearch}
        count={data?.count ?? 0}
        factorCount={modelStats.factorCount}
        qualityFactorCount={modelStats.qualityFactorCount}
        valuationFactorCount={modelStats.valuationFactorCount}
        updatedAt={data?.updatedAt ?? ''}
        viewMode={viewMode}
        onViewModeChange={(mode) => {
          setPillarView(null);
          setHighlightedFactor(null);
          setSelectedSymbol(null);
          setSearch('');
          setResetKey(k => k + 1);
          setViewMode(mode);
        }}
        enabledFactors={enabledFactors}
        onToggleFactor={toggleFactor}
        onResetFactors={resetFactors}
        portfolioSymbols={portfolioSymbols}
        onPortfolioChange={handlePortfolioChange}
        portfolioIsolated={portfolioIsolated}
        onTogglePortfolioIsolate={() => {
          // Mutually exclusive with alpha-zone iso — toggling one clears the other.
          setAlphaZoneIsolated(false);
          setPortfolioIsolated(prev => !prev);
        }}
        alphaZoneSize={alphaZoneStocks.size}
        alphaZoneIsolated={alphaZoneIsolated}
        onToggleAlphaZoneIsolate={() => {
          setPortfolioIsolated(false);
          setAlphaZoneIsolated(prev => !prev);
        }}
        pillarWeights={pillarWeights}
        onPillarWeightChange={onPillarWeightChange}
        onResetWeights={resetWeights}
        onPillarClick={(p: string) => setPillarView(p)}
        allStocks={data?.stocks?.map(s => ({ symbol: s.symbol, name: s.name }))}
        onCloseMobile={() => setSidebarOpen(false)}
        highlightedFactor={highlightedFactor}
        onFactorHighlight={(key) => {
          // Selecting a leaf factor clears any pillar highlight (and vice versa).
          setHighlightedPillar(null);
          setHighlightedFactor(prev => prev === key ? null : key);
        }}
        highlightedPillar={highlightedPillar}
        onPillarHighlight={(pillar) => {
          setHighlightedFactor(null);
          setHighlightedPillar(prev => prev === pillar ? null : pillar);
        }}
      />
      </div>
      <HomeViewport
        viewMode={viewMode}
        pillarView={pillarView}
        onPillarBack={() => setPillarView(null)}
        filtered={filtered}
        filteredHistorical={filteredHistorical}
        stocksToUse={stocksToUse}
        search={search}
        pinnedSymbols={pinnedSymbols}
        onTogglePin={togglePin}
        onSelectStock={(symbol) => {
          if (pillarView) setPillarView(null);
          selectStock(symbol);
        }}
        selectedSymbol={selectedSymbol}
        selectedStock={selectedStock}
        selectedTechnicalData={selectedTechnicalData}
        onCloseStockDetail={() => setSelectedSymbol(null)}
        resetKey={resetKey}
        totalCount={data?.count ?? 0}
        technicalData={technicalData}
        technicalWeeklyData={technicalWeeklyData}
        technicalMonthlyData={technicalMonthlyData}
        factorHighlightStocks={factorHighlightStocks}
        sectorTopComposite={sectorTopComposite}
        alphaZoneStocks={alphaZoneStocks}
        enteredAlphaSymbols={enteredAlphaSymbols}
        exitedAlphaSymbols={exitedAlphaSymbols}
        historicalPeriod={historicalPeriod}
        historicalDate={historicalDate}
        historicalLoading={historicalLoading}
        onHistoricalPeriodChange={handleHistoricalPeriod}
        updatedAt={data?.updatedAt}
        portfolioIsolated={portfolioIsolated}
        selectedSectorsCount={selectedSectors.size}
        projectionHorizon={projectionHorizon}
        onProjectionChange={setProjectionHorizon}
        vMode={vMode}
        onVModeChange={setVMode}
        backtestData={backtestData}
      />
      </div>
      </div>
    </>
  );
}
